import React, { useRef } from 'react';
import { Button } from '../button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../dropdown-menu';
import { useLocalization } from '../../hooks/useLocalization';

interface LanguageSwitcherProps {
    className?: string;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ className }) => {
    const { currentLanguage, changeLanguage, getSupportedLanguages } = useLocalization();
    const supportedLanguages = getSupportedLanguages();
    const buttonRef = useRef<HTMLButtonElement>(null);

    const currentLang = supportedLanguages.find(lang => lang.code === currentLanguage) || supportedLanguages[0];

    const handleLanguageChange = (languageCode: string) => {
        changeLanguage(languageCode);
        // Remove focus from the button after language change
        setTimeout(() => {
            // Blur the button directly using ref
            if (buttonRef.current) {
                buttonRef.current.blur();
                // Force remove focus by clicking on body
                buttonRef.current.blur();
            }
            // Also blur any currently focused element
            if (document.activeElement && document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }
            // Force focus removal by clicking on body
            document.body.click();
        }, 100);
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button 
                    ref={buttonRef}
                    variant="outline" 
                    size="sm" 
                    className={`flex items-center gap-1 border border-gray-200 dark:border-gray-500 focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-gray-200 dark:focus:border-gray-500 hover:border-gray-200 dark:hover:border-gray-500 active:border-gray-200 dark:active:border-gray-500 ${className}`}
                    style={{
                        outline: 'none',
                        boxShadow: 'none'
                    }}
                    onBlur={() => {
                        // Ensure no focus styles remain
                        if (buttonRef.current) {
                            buttonRef.current.style.outline = 'none';
                            buttonRef.current.style.boxShadow = 'none';
                        }
                    }}
                >
                    <span role="img" aria-label="flag">{currentLang.flag}</span>
                    <span className="uppercase">{currentLang.code}</span>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-full min-w-[var(--radix-dropdown-menu-trigger-width)]">
                {supportedLanguages.map((language) => (
                    <DropdownMenuItem
                        key={language.code}
                        onClick={() => handleLanguageChange(language.code)}
                        className={currentLanguage === language.code ? 'bg-blue-50 dark:bg-blue-900' : ''}
                    >
                        <span role="img" aria-label="flag">{language.flag}</span>
                        <span className="ml-2">{language.name}</span>
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default LanguageSwitcher; 
